import KidScientist from './KidScientist';
import Beaker from './Beaker';
import ShelfGlassware from './ShelfGlassware';
import WallCalendar from './WallCalendar';
import ReactionEffect, { ReactionEffectGradients } from './ReactionEffect';
import { SCENE_CLASSES } from '../../constants/classNames';
import { MIX_PHASES } from '../../constants/labPhases';
import { ASSISTANT_NAME } from '../../constants/config';
import { joinClassNames } from '../../utils/classNameUtils';
import useTalkingMouth from '../../hooks/useTalkingMouth';
import { getBeakerLiquidColor, getFlaskAppearance, getKidExpression } from '../../utils/sceneUtils';
import '../../styles/LabScene.css';

function LabBackground() {
  return (
    <>
      <rect className={SCENE_CLASSES.wall} x="0" y="0" width="480" height="300" fill="#e8f0ff" />
      <rect className={SCENE_CLASSES.floor} x="0" y="296" width="480" height="24" fill="#cfd9ec" />
      <g className={SCENE_CLASSES.shelf}>
        <rect x="20" y="62" width="96" height="6" rx="2" fill="#b98b64" />
        <ShelfGlassware />
      </g>
      <WallCalendar />
    </>
  );
}

// Fizzy, the flasks and the beaker are drawn a bit smaller than the room, scaled
// around the middle of the table top so the beaker still stands on the table.
const TABLE_TOP = { x: 240, y: 222 };
const CHARACTER_SCALE = 0.85;
const CHARACTER_TRANSFORM = `translate(${TABLE_TOP.x} ${TABLE_TOP.y}) scale(${CHARACTER_SCALE}) translate(${-TABLE_TOP.x} ${-TABLE_TOP.y})`;

/**
 * Lab table with a front cover board (a closed front panel) below the table top.
 */
function LabTable() {
  return (
    <g className={SCENE_CLASSES.table}>
      <rect x="90" y="236" width="300" height="60" fill="#b07a4f" />
      <rect x="100" y="244" width="136" height="44" rx="3" fill="none" stroke="#9a6841" strokeWidth="2" />
      <rect x="244" y="244" width="136" height="44" rx="3" fill="none" stroke="#9a6841" strokeWidth="2" />
      <circle cx="226" cy="266" r="2.5" fill="#8a5c38" />
      <circle cx="254" cy="266" r="2.5" fill="#8a5c38" />
      <rect x="84" y="222" width="312" height="16" rx="4" fill="#c98b5a" />
      <rect x="84" y="222" width="312" height="4" rx="2" fill="#dba377" />
    </g>
  );
}

/**
 * Two drops flying from the flasks into the beaker while mixing.
 */
function MixingDrips({ leftColor, rightColor }) {
  return (
    <>
      <circle className={joinClassNames(SCENE_CLASSES.drip, SCENE_CLASSES.dripLeft)} cx="150" cy="160" r="6" fill={leftColor} />
      <circle className={joinClassNames(SCENE_CLASSES.drip, SCENE_CLASSES.dripRight)} cx="330" cy="160" r="6" fill={rightColor} />
    </>
  );
}

/**
 * Responsive SVG lab: the kid scientist behind a table, holding the two picked
 * substances in flasks, with a beaker that reacts when they are mixed.
 */
export default function LabScene({ selectedSubstances, reaction, mixPhase, mixCount, isTalking }) {
  const leftFlask = getFlaskAppearance(selectedSubstances[0]);
  const rightFlask = getFlaskAppearance(selectedSubstances[1]);
  const isMixing = mixPhase === MIX_PHASES.PLAYING;
  const mouthShape = useTalkingMouth(isTalking);

  return (
    <svg
      className={joinClassNames(SCENE_CLASSES.root, isMixing && SCENE_CLASSES.mixing, isTalking && SCENE_CLASSES.talking)}
      viewBox="0 0 480 320"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${ASSISTANT_NAME} the kid scientist holding ${leftFlask.label} and ${rightFlask.label} behind a lab table with a beaker`}
    >
      <defs>
        <ReactionEffectGradients />
      </defs>
      <LabBackground />
      <g transform={CHARACTER_TRANSFORM}>
        <KidScientist
          leftFlask={leftFlask}
          rightFlask={rightFlask}
          mouthShape={mouthShape}
          expression={getKidExpression(reaction, mixPhase)}
        />
      </g>
      <LabTable />
      <g transform={CHARACTER_TRANSFORM}>
        <Beaker liquidColor={getBeakerLiquidColor(reaction, mixPhase)} />
        {isMixing && <MixingDrips key={`drips-${mixCount}`} leftColor={leftFlask.liquidColor} rightColor={rightFlask.liquidColor} />}
        {isMixing && reaction && <ReactionEffect key={`effect-${mixCount}`} effect={reaction.effect} />}
      </g>
    </svg>
  );
}
